import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Plus, Pencil, Trash2, Loader2, Palette, ChevronRight } from 'lucide-react';
import api from '../../services/api';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from '../../components/ui/alert-dialog';
import { useToast } from '../../hooks/use-toast';

const CosmeticsAdmin = () => {
  const { toast } = useToast();
  const [areas, setAreas] = useState([]);
  const [types, setTypes] = useState([]);
  const [loading, setLoading] = useState(true);

  // Area dialog
  const [areaOpen, setAreaOpen] = useState(false);
  const [areaForm, setAreaForm] = useState({ id: '', name: '' });
  const [areaSaving, setAreaSaving] = useState(false);
  const [areaDeleting, setAreaDeleting] = useState(null);

  // Type dialog
  const [typeOpen, setTypeOpen] = useState(false);
  const [typeForm, setTypeForm] = useState({ id: '', name: '', areaId: '' });
  const [typeSaving, setTypeSaving] = useState(false);
  const [typeDeleting, setTypeDeleting] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [a, t] = await Promise.all([api.get('/cosmetic-areas'), api.get('/cosmetic-types')]);
      setAreas(a.data);
      setTypes(t.data);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const typesByArea = useMemo(() => {
    const map = {};
    types.forEach(t => { (map[t.areaId] = map[t.areaId] || []).push(t); });
    return map;
  }, [types]);

  // --- Areas ---
  const openNewArea = () => { setAreaForm({ id: '', name: '' }); setAreaOpen(true); };
  const openEditArea = (a) => { setAreaForm({ ...a }); setAreaOpen(true); };
  const saveArea = async (e) => {
    e.preventDefault();
    if (!areaForm.name.trim()) return toast({ title: 'Nombre requerido' });
    setAreaSaving(true);
    try {
      if (areaForm.id) await api.put(`/cosmetic-areas/${areaForm.id}`, { name: areaForm.name.trim() });
      else await api.post('/cosmetic-areas', { name: areaForm.name.trim() });
      toast({ title: areaForm.id ? 'Área actualizada' : 'Área creada' });
      setAreaOpen(false);
      await load();
    } catch (err) {
      toast({ title: 'Error', description: err?.response?.data?.detail || 'No se pudo guardar' });
    } finally { setAreaSaving(false); }
  };
  const confirmDeleteArea = async () => {
    try {
      await api.delete(`/cosmetic-areas/${areaDeleting.id}`);
      toast({ title: 'Área eliminada' });
      setAreaDeleting(null);
      await load();
    } catch (err) {
      toast({ title: 'No se pudo eliminar', description: err?.response?.data?.detail });
    }
  };

  // --- Types ---
  const openNewType = (areaId = '') => { setTypeForm({ id: '', name: '', areaId: areaId || areas[0]?.id || '' }); setTypeOpen(true); };
  const openEditType = (t) => { setTypeForm({ ...t }); setTypeOpen(true); };
  const saveType = async (e) => {
    e.preventDefault();
    if (!typeForm.name.trim() || !typeForm.areaId) return toast({ title: 'Nombre y área requeridos' });
    setTypeSaving(true);
    try {
      const payload = { name: typeForm.name.trim(), areaId: typeForm.areaId };
      if (typeForm.id) await api.put(`/cosmetic-types/${typeForm.id}`, payload);
      else await api.post('/cosmetic-types', payload);
      toast({ title: typeForm.id ? 'Tipo actualizado' : 'Tipo creado' });
      setTypeOpen(false);
      await load();
    } catch (err) {
      toast({ title: 'Error', description: err?.response?.data?.detail || 'No se pudo guardar' });
    } finally { setTypeSaving(false); }
  };
  const confirmDeleteType = async () => {
    try {
      await api.delete(`/cosmetic-types/${typeDeleting.id}`);
      toast({ title: 'Tipo eliminado' });
      setTypeDeleting(null);
      await load();
    } catch (err) {
      toast({ title: 'No se pudo eliminar', description: err?.response?.data?.detail });
    }
  };

  if (loading) return <div className="flex items-center justify-center py-20"><Loader2 className="h-8 w-8 animate-spin text-emerald-700" /></div>;

  return (
    <div className="space-y-6">
      <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
        <div className="flex items-center gap-3">
          <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-pink-100 text-pink-700">
            <Palette className="h-5 w-5" />
          </div>
          <div>
            <h1 className="text-2xl font-bold text-slate-900">Cosméticos</h1>
            <p className="text-sm text-slate-500">Administra las Áreas (Ojos, Rostro...) y Tipos (Máscara, Sombra...) usadas en los productos cosméticos.</p>
          </div>
        </div>
        <div className="flex gap-2">
          <Button onClick={openNewArea} variant="outline" className="gap-2" data-testid="new-area-btn">
            <Plus className="h-4 w-4" /> Nueva área
          </Button>
          <Button onClick={() => openNewType()} className="gap-2 bg-emerald-700 text-white hover:bg-emerald-800" data-testid="new-type-btn">
            <Plus className="h-4 w-4" /> Nuevo tipo
          </Button>
        </div>
      </div>

      {areas.length === 0 ? (
        <div className="rounded-xl border border-dashed border-slate-200 bg-white p-10 text-center text-slate-500">
          Aún no hay áreas. Crea la primera (ej. "Ojos", "Rostro") para empezar.
        </div>
      ) : (
        <div className="space-y-3">
          {areas.map(a => {
            const list = typesByArea[a.id] || [];
            return (
              <div key={a.id} className="overflow-hidden rounded-xl border border-slate-200 bg-white shadow-sm">
                <div className="flex items-center justify-between border-b border-slate-100 bg-slate-50/50 px-5 py-3">
                  <div className="flex items-center gap-2">
                    <ChevronRight className="h-4 w-4 text-slate-400" />
                    <h3 className="text-base font-semibold text-slate-900">{a.name}</h3>
                    <span className="rounded-full bg-pink-100 px-2 py-0.5 text-xs font-medium text-pink-700">{list.length} tipo(s)</span>
                  </div>
                  <div className="flex gap-1">
                    <Button variant="ghost" size="sm" onClick={() => openNewType(a.id)} className="gap-1 text-emerald-700 hover:bg-emerald-50">
                      <Plus className="h-3.5 w-3.5" /> Tipo
                    </Button>
                    <Button variant="ghost" size="icon" onClick={() => openEditArea(a)} className="text-slate-500 hover:text-emerald-700">
                      <Pencil className="h-4 w-4" />
                    </Button>
                    <Button variant="ghost" size="icon" onClick={() => setAreaDeleting(a)} className="text-slate-500 hover:text-red-600">
                      <Trash2 className="h-4 w-4" />
                    </Button>
                  </div>
                </div>
                {list.length === 0 ? (
                  <div className="px-5 py-6 text-center text-sm text-slate-400">Sin tipos aún. Crea uno con "+ Tipo".</div>
                ) : (
                  <ul className="divide-y divide-slate-100">
                    {list.map(t => (
                      <li key={t.id} className="flex items-center justify-between px-5 py-3 hover:bg-slate-50">
                        <span className="text-sm text-slate-800">{t.name}</span>
                        <div className="flex gap-1">
                          <Button variant="ghost" size="icon" onClick={() => openEditType(t)} className="text-slate-500 hover:text-emerald-700">
                            <Pencil className="h-4 w-4" />
                          </Button>
                          <Button variant="ghost" size="icon" onClick={() => setTypeDeleting(t)} className="text-slate-500 hover:text-red-600">
                            <Trash2 className="h-4 w-4" />
                          </Button>
                        </div>
                      </li>
                    ))}
                  </ul>
                )}
              </div>
            );
          })}
        </div>
      )}

      {/* Area dialog */}
      <Dialog open={areaOpen} onOpenChange={setAreaOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader><DialogTitle>{areaForm.id ? 'Editar área' : 'Nueva área'}</DialogTitle></DialogHeader>
          <form onSubmit={saveArea} className="space-y-4">
            <div className="space-y-2">
              <Label>Nombre</Label>
              <Input value={areaForm.name} onChange={(e) => setAreaForm({ ...areaForm, name: e.target.value })} className="h-11" placeholder="Ej. Ojos" autoFocus />
            </div>
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setAreaOpen(false)}>Cancelar</Button>
              <Button type="submit" disabled={areaSaving} className="bg-emerald-700 text-white hover:bg-emerald-800">
                {areaSaving && <Loader2 className="mr-2 h-4 w-4 animate-spin" />} Guardar
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Type dialog */}
      <Dialog open={typeOpen} onOpenChange={setTypeOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader><DialogTitle>{typeForm.id ? 'Editar tipo' : 'Nuevo tipo'}</DialogTitle></DialogHeader>
          <form onSubmit={saveType} className="space-y-4">
            <div className="space-y-2">
              <Label>Área</Label>
              <Select value={typeForm.areaId} onValueChange={(v) => setTypeForm({ ...typeForm, areaId: v })}>
                <SelectTrigger className="h-11"><SelectValue placeholder="Selecciona área" /></SelectTrigger>
                <SelectContent>
                  {areas.map(a => <SelectItem key={a.id} value={a.id}>{a.name}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label>Nombre</Label>
              <Input value={typeForm.name} onChange={(e) => setTypeForm({ ...typeForm, name: e.target.value })} className="h-11" placeholder="Ej. Máscara" autoFocus />
            </div>
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setTypeOpen(false)}>Cancelar</Button>
              <Button type="submit" disabled={typeSaving} className="bg-emerald-700 text-white hover:bg-emerald-800">
                {typeSaving && <Loader2 className="mr-2 h-4 w-4 animate-spin" />} Guardar
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      <AlertDialog open={!!areaDeleting} onOpenChange={(v) => !v && setAreaDeleting(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>¿Eliminar área?</AlertDialogTitle>
            <AlertDialogDescription>Se eliminará <strong>{areaDeleting?.name}</strong>. No podrá eliminarse si hay productos o tipos vinculados.</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancelar</AlertDialogCancel>
            <AlertDialogAction onClick={confirmDeleteArea} className="bg-red-600 hover:bg-red-700">Eliminar</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <AlertDialog open={!!typeDeleting} onOpenChange={(v) => !v && setTypeDeleting(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>¿Eliminar tipo?</AlertDialogTitle>
            <AlertDialogDescription>Se eliminará <strong>{typeDeleting?.name}</strong>. No podrá eliminarse si hay productos usándolo.</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancelar</AlertDialogCancel>
            <AlertDialogAction onClick={confirmDeleteType} className="bg-red-600 hover:bg-red-700">Eliminar</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};

export default CosmeticsAdmin;
